import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { request } from './helper';
import { LeadsState, Lead, Note, AiCall } from '@/types'; // Import your types

// **Thunks**
export const leadsGetAll = createAsyncThunk(
  'leads/getAll',
  async ({ page = 1, perPage = 50 }: { page?: number; perPage?: number }, { rejectWithValue }) => {
    const response = await request(`api/lead?page=${page}&perPage=${perPage}`);
    if (response.success) {
      return {
        data: response.data,
        pagination: response.pagination,
      };
    }
    return rejectWithValue(response.error);
  }
);

export const leadsGetById = createAsyncThunk<Lead, number>('leads/getById', async (id) => {
  const response = await request(`api/lead/${id}`);
  return response.success ? response.data : null;
});

export const leadsCreateNote = createAsyncThunk<Note[], { id: number; note: string }>(
  'leads/createNote',
  async ({ id, note }) => {
    const response = await request(`api/lead-note/${id}`, 'POST', { note });
    return response.success ? response.data : [];
  }
);

export const leadsUpdateNote = createAsyncThunk<Lead, { id: number; note: string }>(
  'leads/updateNote',
  async ({ id, note }) => {
    const response = await request(`api/lead-note/${id}`, 'PUT', { note });
    return response.success ? response.data : [];
  }
);

export const leadsDeleteNote = createAsyncThunk<Lead, { id: number }>(
  'leads/deleteNote',
  async ({ id }) => {
    const response = await request(`api/lead-note/${id}`, 'DELETE');
    return response.success ? response.data : [];
  }
);

export const leadUpdateAiCall = createAsyncThunk<Lead, { id: number; data: Partial<AiCall> }>(
  'ai-call/update',
  async ({ id, data }) => {
    const response = await request(`api/ai-call/${id}`, 'PUT', data);
    return response.success ? response.data : [];
  }
);

export const leadUpdate = createAsyncThunk<Lead, { id: number; data: any }>(
  'leads/update',
  async ({ id, data }) => {
    const response = await request(`api/lead/${id}`, 'PUT', data);
    return response.success ? response.data : [];
  }
);

export const leadsChangeLSAStatus = createAsyncThunk<Lead, { id: number; data: any }>(
  'leads/changeLSAStatus',
  async ({ id, data }) => {
    const response = await request(`api/lead/lsa-status/${id}`, 'POST', data);
    return response.success ? response.data : [];
  }
);

export const leadsCreateReminder = createAsyncThunk(
  'alerts/createReminder',
  async ({id, data}: {id: number, data: any}, { rejectWithValue }) => {
    const response = await request(`api/lead/reminder/${id}`, 'POST', data);
    return response.success ? response.data : rejectWithValue(response.error);
  }
);

export const leadsDeleteReminder = createAsyncThunk(
  'alerts/getReminder',
  async (id: number, { rejectWithValue }) => {
    const response = await request(`api/lead/reminder/${id}`, 'DELETE');
    return response.success ? response.data : rejectWithValue(response.error || 'Failed to fetch reminder');
});

export const updateLeadStatus = createAsyncThunk(
  'leads/updateStatus',
  async ({ leadId, status }: { leadId: number; status: string }) => {
    const response = await request(`api/lead/${leadId}/status`, 'PUT', { status });
    return response.data;
  }
);

export const markLeadMessagesAsRead = createAsyncThunk(
  'leads/markMessagesAsRead',
  async (leadId: number) => {
    const response = await request(`api/message/${leadId}/mark-as-read`, 'POST');
    return response.data;
  }
);

// **Initial State**
const initialState: LeadsState = {
  leads: [],
  lead: null,
  status: 'idle',
  aiCallLoading: false,
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0,
  },
};

// **Slice**
export const leadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    clearLeads: (state) => {
      state.leads = [];
      state.lead = null;
    },
    updateLead(state, action: PayloadAction<Lead>) {
      const updatedLead = action.payload;
      // Update the specific lead in the leads array
      state.leads = state.leads.map((lead) =>
        lead.id === updatedLead.id ? updatedLead : lead
      );

      // Update the current lead if it matches
      if (state.lead && state.lead.id === updatedLead.id) {
        state.lead = updatedLead;
      }
    },
  },
  extraReducers: (builder) => {
    // Get All Leads
    builder
      .addCase(leadsGetAll.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(leadsGetAll.fulfilled, (state, action) => {
        state.leads = action.payload.data || [];
        state.status = 'success';
        state.pagination = {
          current_page: action.payload.pagination.current_page,
          last_page: action.payload.pagination.last_page,
          per_page: action.payload.pagination.per_page,
          total: action.payload.pagination.total,
        };
      })
      .addCase(leadsGetAll.rejected, (state) => {
        state.status = 'failed';
      });

    // Get Lead By ID
    builder
      .addCase(leadsGetById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(leadsGetById.fulfilled, (state, { payload }) => {
        state.status = 'success';
        state.lead = payload;
      })
      .addCase(leadsGetById.rejected, (state) => {
        state.status = 'failed';
      });

    // Create Note for Lead
    builder
      .addCase(leadsCreateNote.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(leadsCreateNote.fulfilled, (state, { payload }) => {
        state.status = 'success';
        if (state.lead) {
          state.lead.notes = payload;
        }
      })
      .addCase(leadsCreateNote.rejected, (state) => {
        state.status = 'failed';
      });

    // Create Note for Lead
    builder
      .addCase(leadUpdateAiCall.pending, (state) => {
        state.aiCallLoading = true;
      })
      .addCase(leadUpdateAiCall.fulfilled, (state, { payload }) => {
        state.aiCallLoading = false;
        if (state.lead) {
          state.lead = payload;
        }
      })
      .addCase(leadUpdateAiCall.rejected, (state) => {
        state.aiCallLoading = false;
      });
    
      // Update Note for Lead
    builder
    .addCase(leadsUpdateNote.pending, (state) => {
      state.status = 'loading';
    })
    .addCase(leadsUpdateNote.fulfilled, (state, { payload }) => {
      state.status = 'success';
      state.lead = payload;
    })
    .addCase(leadsUpdateNote.rejected, (state) => {
      state.status = 'failed';
    });

    // Delete Note for Lead
    builder
      .addCase(leadsDeleteNote.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(leadsDeleteNote.fulfilled, (state, { payload }) => {
        state.status = 'success';
        state.lead = payload;
        // if (state.lead && state.lead.notes) {
        //   state.lead.notes = state.lead.notes.filter(
        //     (note) => !payload.find((deletedNote) => deletedNote.id === note.id)
        //   );
        // }
      })
      .addCase(leadsDeleteNote.rejected, (state) => {
        state.status = 'failed';
      });

      // Update Lead
      builder
      .addCase(leadUpdate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(leadUpdate.fulfilled, (state, { payload }) => {
        state.status = 'success';
        const leadIndex = state.leads.findIndex((lead) => lead.id === payload.id);
        if (leadIndex !== -1) {
          state.leads[leadIndex] = payload; // Update the specific lead
        }
        if (state.lead && state.lead.id === payload.id) {
          state.lead = payload; // Update the active lead if applicable
        }
      })
      .addCase(leadUpdate.rejected, (state) => {
        state.status = 'failed';
      });
      
      // Create reminder
      builder.addCase(leadsCreateReminder.fulfilled, (state, { payload }) => {
        state.lead = payload;
      });

      // Delete Reminder
      builder
        .addCase(leadsDeleteReminder.fulfilled, (state, { payload }) => {
          state.lead = payload;
          state.status = 'success';
        })

      // Change LSA Status
      builder
        .addCase(leadsChangeLSAStatus.fulfilled, (state, { payload }) => {
          state.lead = payload;
          state.status = 'success';
        });

    // Update Lead Status
    builder
      .addCase(updateLeadStatus.fulfilled, (state, { payload }) => {
        // Update the lead in the leads array
        const index = state.leads.findIndex(lead => lead.id === payload.id);
        if (index !== -1) {
          state.leads[index] = payload;
        }
        // Update the current lead if it matches
        if (state.lead?.id === payload.id) {
          state.lead = payload;
        }
      });

    // Mark Messages as Read
    builder
      .addCase(markLeadMessagesAsRead.fulfilled, (state, { payload }) => {
        // Update the lead in the leads array
        const index = state.leads.findIndex(lead => lead.id === payload.id);
        if (index !== -1) {
          state.leads[index] = payload;
        }
        // Update the current lead if it matches
        if (state.lead?.id === payload.id) {
          state.lead = payload;
        }
      });
  },
});

export const { updateLead, clearLeads } = leadsSlice.actions;

// **Export Reducer**
export default leadsSlice.reducer;